import { useState, useEffect } from 'react';
import { useUserCategories, UserCategory } from '../../hooks/useUserCategories';
import { useAuth } from '../../hooks/useAuth';

// ナガヤマのユーザーID（このユーザーが作成した科目・カテゴリは他ユーザーが削除・編集不可）
const NAGAYAMA_USER_ID = '34730814-27c6-476d-98d1-90d8c95649f1';

interface SubjectManagerModalProps {
  onClose: () => void;
  onUpdated?: () => void;
}

export default function SubjectManagerModal({ onClose, onUpdated }: SubjectManagerModalProps) {
  const [activeTab, setActiveTab] = useState<'subject' | 'category'>('subject');

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <i className="ri-folder-settings-line text-teal-500"></i>
              科目・カテゴリ管理
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              追加した科目・カテゴリは全コンテンツで共通利用されます
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600 dark:text-gray-300"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 shrink-0">
          <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
            <button
              onClick={() => setActiveTab('subject')}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'subject'
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <i className="ri-book-line mr-1.5"></i>科目
            </button>
            <button
              onClick={() => setActiveTab('category')}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'category'
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <i className="ri-price-tag-3-line mr-1.5"></i>カテゴリ
            </button>
          </div>
        </div>

        {/* Content */}
        <CategoryListPanel
          key={activeTab}
          categoryType={activeTab}
          onUpdated={onUpdated}
        />
      </div>
    </div>
  );
}

function CategoryListPanel({
  categoryType,
  onUpdated,
}: {
  categoryType: 'subject' | 'category';
  onUpdated?: () => void;
}) {
  const { user } = useAuth();
  const {
    categories,
    loading,
    searchQuery,
    setSearchQuery,
    addCategory,
    toggleFavorite,
    deleteCategory,
  } = useUserCategories(categoryType);

  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleAdd = async () => {
    if (!newName.trim()) {
      setError('名前を入力してください');
      return;
    }
    setAdding(true);
    setError('');
    try {
      await addCategory(newName.trim());
      setNewName('');
      setToast(`「${newName.trim()}」を追加しました`);
      onUpdated?.();
    } catch (err: any) {
      if (err?.code === '23505') {
        setError('この名前は既に存在します');
      } else {
        setError('追加に失敗しました');
      }
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (cat: UserCategory) => {
    // ナガヤマが作成した科目・カテゴリはナガヤマ本人のみ削除可能
    if (cat.user_id === NAGAYAMA_USER_ID && user?.id !== NAGAYAMA_USER_ID) {
      setError('このカテゴリはオーナーのみ削除できます');
      return;
    }
    // 自分が作成したレコードのみ削除可能
    if (cat.user_id !== user?.id) {
      setError('他のユーザーが作成した科目・カテゴリは削除できません');
      return;
    }
    try {
      await deleteCategory(cat.id);
      setConfirmDeleteId(null);
      setToast(`「${cat.category_name}」を削除しました`);
      onUpdated?.();
    } catch {
      setError('削除に失敗しました');
    }
  };

  const handleToggleFav = async (cat: UserCategory) => {
    try {
      await toggleFavorite(cat.id, cat.is_favorite);
      onUpdated?.();
    } catch {
      // silent
    }
  };

  const label = categoryType === 'subject' ? '科目' : 'カテゴリ';

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className="mx-6 mt-3">
          <div className="px-4 py-2 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-400 text-sm rounded-lg flex items-center gap-2">
            <i className="ri-checkbox-circle-fill"></i>
            {toast}
          </div>
        </div>
      )}

      {/* Add new */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
          新しい{label}を追加
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
            placeholder={`${label}名を入力...`}
            maxLength={50}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white"
          />
          <button
            onClick={handleAdd}
            disabled={adding}
            className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {adding ? '...' : '追加'}
          </button>
        </div>
        {error && (
          <p className="text-xs text-red-500 mt-1.5">{error}</p>
        )}
      </div>

      {/* Search */}
      <div className="px-6 py-3 shrink-0">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`${label}を検索...`}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 dark:text-white"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            読み込み中...
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            {searchQuery ? '該当する項目がありません' : `${label}がありません`}
          </div>
        ) : (
          <div className="space-y-1.5">
            {categories.map((cat) => {
              // 自分が作成したレコードかどうかを厳密に判定
              const isMyRecord = user?.id === cat.user_id;
              // ナガヤマが作成したレコードかどうか
              const isNagayamaRecord = cat.user_id === NAGAYAMA_USER_ID;
              // 削除ボタンを表示するか：自分のレコードかつ（ナガヤマ作成でないか、自分がナガヤマ本人）
              const canDelete = isMyRecord && (!isNagayamaRecord || user?.id === NAGAYAMA_USER_ID);

              return (
                <div
                  key={cat.id}
                  className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl group hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span className="text-sm text-gray-800 dark:text-white flex items-center gap-2">
                    {cat.is_favorite && (
                      <i className="ri-star-fill text-yellow-500 text-sm"></i>
                    )}
                    {cat.category_name}
                    {/* ナガヤマが作成したレコードで自分がナガヤマでない場合はロックアイコンを表示 */}
                    {isNagayamaRecord && user?.id !== NAGAYAMA_USER_ID && (
                      <span className="flex items-center gap-1 text-xs text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full">
                        <i className="ri-lock-fill text-xs"></i>
                        管理者作成
                      </span>
                    )}
                    {/* 自分のレコードでもナガヤマのレコードでもない場合 */}
                    {!isMyRecord && !isNagayamaRecord && (
                      <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                        <i className="ri-lock-line text-xs"></i>
                        他ユーザー作成
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    {/* お気に入りボタン：全員表示 */}
                    <button
                      onClick={() => handleToggleFav(cat)}
                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer"
                      title={cat.is_favorite ? 'お気に入り解除' : 'お気に入り登録'}
                    >
                      <i className={`${cat.is_favorite ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-gray-400'} text-sm`}></i>
                    </button>
                    {/* 削除ボタン：削除権限がある場合のみ表示 */}
                    {canDelete && (
                      confirmDeleteId === cat.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer whitespace-nowrap"
                          >
                            いいえ
                          </button>
                          <button
                            onClick={() => handleDelete(cat)}
                            className="px-2 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded cursor-pointer whitespace-nowrap"
                          >
                            削除
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(cat.id)}
                          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors cursor-pointer"
                          title="削除"
                        >
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
